// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { LogFactory } from './../services/debug';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { NEVER, Observable, of } from 'rxjs';
import { catchError, flatMap, map, tap } from 'rxjs/operators';
import * as uuidv4 from 'uuid/v4';
import {
  AuthActionTypes,
  LogoutAction,
  LoginAction,
  Token,
  TriggerLoginAction,
  FailLoginRefreshAction
} from './auth.actions';
import { GoAction } from './router.actions';
import { AuthService } from '../services/auth.service';
import { AUTH_ROUTE, LOGOUT_ROUTE } from '../utilities';
import { isNavigationActionToRoute, onStoreInitialized } from '../utilities/observables';
import { SPAAuthService } from '../services/spa-auth.service';

export const STORAGE_TOKEN = new InjectionToken<Storage>('storage');

export const TOKEN_STORAGE_KEY = 'token';
export const REQUEST_ID_KEY = 'requestId';

const log = LogFactory('AuthEffect');

function parseToken(token: string): Token {
  const tokenObj = JSON.parse(token);
  return {
    accessToken: tokenObj.accessToken,
    idToken: tokenObj.idToken,
    expiresAt: new Date(tokenObj.expiresAt)
  };
}

function serializeToken(token: Token): string {
  return JSON.stringify(token);
}

function loadTokenFromStorage(storage: Storage): Token | undefined {
  const tokenString = storage.getItem(TOKEN_STORAGE_KEY);
  if (tokenString === null) {
    return undefined;
  }
  return parseToken(tokenString);
}

function saveTokenToStorage(token: Token, storage: Storage) {
  storage.setItem(TOKEN_STORAGE_KEY, serializeToken(token));
}

function removeTokenFromStorage(storage: Storage) {
  storage.removeItem(TOKEN_STORAGE_KEY);
}

function saveRequestIdToStorage(requestId: string, storage: Storage) {
  storage.setItem(REQUEST_ID_KEY, requestId);
}

function loadRequestIdFromStorage(storage: Storage): string {
  const requestId = storage.getItem(REQUEST_ID_KEY);
  if (requestId === null) {
    throw Error('RequestId missing from storage');
  }
  return requestId;
}

function removeRequestIdFromStorage(storage: Storage) {
  storage.removeItem(REQUEST_ID_KEY);
}

function generateRequestId(): string {
  return uuidv4();
}

@Injectable()
export class AuthEffects {
  constructor(
    private authService: SPAAuthService,
    @Inject(STORAGE_TOKEN) private storage: Storage,
    private actions$: Actions
  ) {}
}



// WEBPACK FOOTER //
// ./src/app/core/state/auth.effects.ts